import { TFunction } from 'i18next';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import {
    CountryInterface,
    FilterFieldInterface,
    FiltersInterface,
    FilterSize,
    LabelSize,
    StateInterface,
} from 'components/interfaces/GeneralInterface';
import { getMainFilter } from 'components/tables/filters/Filters';
import { ClubListItemInterface } from 'components/interfaces/ClubInterface';
import { PERSON_FILTER_KEYS } from 'constants/filterKeysConstants';
import { DisciplineEnum, TNamespaceEnum } from 'types/types';
import { sortCountriesForFilter } from 'utils/helpers/browser/country';
import {
    findClubsById,
    findClubsByInternalId,
    findLicenseTypes,
    transformClubsToOptions,
} from 'utils/helpers/browser/filter';
import { getGenderSelectValues } from 'utils/helpers/getGenderSelectValues';
import { getOrganizationUnitSelectValues } from 'utils/helpers/getOrganizationUnitSelectValues';
import {
    FieldTypeApiEnum,
    LicenseRoleEnum,
    OrganisationUnitApiInterface,
    PersonRoleNoDisciplineEnum,
} from 'submodules/api_middleware';
import { getMinNumber } from 'utils/helpers/datetime';
import { getTranslation, getTranslationKey } from 'utils/localization';
import { validateSearchQuery } from 'utils/helpers/validateSearchQuery';

export const getPersonFilters = (
    t: TFunction,
    countriesList: CountryInterface[],
    clubList: ClubListItemInterface[],
    searchPlaceholderKey: string,
    advancedFilterKeys: string[] = [],
    organisationUnits?: OrganisationUnitApiInterface[],
    discipline?: DisciplineEnum
): FiltersInterface => {
    const advancedFilters: FilterFieldInterface[] = [];

    advancedFilterKeys.forEach((filterKey) => {
        switch (filterKey) {
            case PERSON_FILTER_KEYS.birth_year:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.birth_year,
                    label: 'filters.year',
                    type: FieldTypeApiEnum.NUMBER,
                    size: FilterSize.THIRD,
                    labelSize: LabelSize.ML,
                    min: getMinNumber(),
                    max: new Date().getFullYear(),
                });
                break;
            case PERSON_FILTER_KEYS.age_from:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.age_from,
                    label: 'Members.ageFrom',
                    type: FieldTypeApiEnum.NUMBER,
                    size: FilterSize.THIRD,
                    labelSize: LabelSize.ML,
                    max: (state: StateInterface) =>
                        state.age_to ? state.age_to : undefined,
                });
                break;
            case PERSON_FILTER_KEYS.age_to:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.age_to,
                    label: 'Members.ageTo',
                    type: FieldTypeApiEnum.NUMBER,
                    size: FilterSize.THIRD,
                    labelSize: LabelSize.ML,
                    min: (state: StateInterface) =>
                        state.age_from ? state.age_from : undefined,
                });
                break;
            case PERSON_FILTER_KEYS.gender:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.gender,
                    label: getTranslationKey(TNamespaceEnum.person, 'gender'),
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.THIRD,
                    values: getGenderSelectValues(t),
                    labelSize: LabelSize.ML,
                });
                break;
            case PERSON_FILTER_KEYS.members:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.members,
                    label: 'MemberProfile.membership',
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.THIRD,
                    values: [
                        {
                            label: t('Players.memberShipActive'),
                            value: 'active',
                        },
                        {
                            label: t('Players.memberShipNotActive'),
                            value: 'notActive',
                        },
                        {
                            label: t('Players.memberShipArchive'),
                            value: 'archive',
                        },
                    ],
                    labelSize: LabelSize.ML,
                });
                break;
            case PERSON_FILTER_KEYS.roles:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.roles,
                    label: getTranslationKey(TNamespaceEnum.person, 'function'),
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.THIRD,
                    values: Object.values(PersonRoleNoDisciplineEnum).reduce(
                        (result, role) => {
                            if (
                                [PersonRoleNoDisciplineEnum.agent].includes(
                                    role
                                )
                            ) {
                                result.push({
                                    label: getTranslation(
                                        t,
                                        TNamespaceEnum.person,
                                        `role.${role}`
                                    ),
                                    value: role,
                                });
                                return result;
                            }
                            if (role !== PersonRoleNoDisciplineEnum.member) {
                                result.push({
                                    label: getTranslation(
                                        t,
                                        TNamespaceEnum.person,
                                        `role.${role}`
                                    ),
                                    value: `${role}-${discipline}`,
                                });
                            }
                            return result;
                        },
                        []
                    ),
                    labelSize: LabelSize.ML,
                });
                break;
            case PERSON_FILTER_KEYS[
                `${discipline}_state` as 'football_state' | 'futsal_state'
            ]:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS[
                        `${discipline}_state` as
                            | 'football_state'
                            | 'futsal_state'
                    ],
                    label: 'MemberProfile.membership',
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.THIRD,
                    values: [
                        {
                            label: t('Members.active'),
                            value: 'active',
                        },
                        {
                            label: t('Members.inactive'),
                            value: 'inactive',
                        },
                    ],
                    labelSize: LabelSize.ML,
                });
                break;
            case PERSON_FILTER_KEYS.owner_club_ids:
                if (clubList) {
                    advancedFilters.push({
                        name: PERSON_FILTER_KEYS.owner_club_ids,
                        label: 'filters.ownerClub',
                        type: FieldTypeApiEnum.AUTOCOMPLETE,
                        size: FilterSize.THIRD,
                        placeholder: 'filters.findByNameId',
                        values: transformClubsToOptions(clubList, 'id'),
                        onSearch: (query: NextApiRequestQuery) =>
                            findClubsById(query),
                        labelSize: LabelSize.ML,
                    });
                }
                break;
            case PERSON_FILTER_KEYS.owner_club_internal_ids:
                if (clubList) {
                    advancedFilters.push({
                        name: PERSON_FILTER_KEYS.owner_club_internal_ids,
                        label: 'filters.ownerClub',
                        type: FieldTypeApiEnum.AUTOCOMPLETE,
                        size: FilterSize.THIRD,
                        placeholder: 'filters.findByNameId',
                        values: transformClubsToOptions(
                            clubList,
                            'internal_id'
                        ),
                        onSearch: (value: NextApiRequestQuery) =>
                            findClubsByInternalId(value),
                        onSearchProperty: 'internal_id',
                        labelSize: LabelSize.ML,
                    });
                }
                break;
            case PERSON_FILTER_KEYS.license_type_referee:
                advancedFilters.push({
                    name: 'license_type[]',
                    label: 'RefereeProfile.licenceType',
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.THIRD,
                    labelSize: LabelSize.ML,
                    initValuesCb: () =>
                        findLicenseTypes(t, LicenseRoleEnum.referee),
                });
                break;
            case PERSON_FILTER_KEYS.license_type_delegate:
                advancedFilters.push({
                    name: 'license_type[]',
                    label: 'RefereeProfile.licenceType',
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.THIRD,
                    labelSize: LabelSize.ML,
                    initValuesCb: () =>
                        findLicenseTypes(t, LicenseRoleEnum.delegate),
                });
                break;
            case PERSON_FILTER_KEYS.photo:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.photo,
                    label: 'Members.foto',
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.THIRD,
                    values: [
                        {
                            label: t('general.yes'),
                            value: 'true',
                        },
                        {
                            label: t('general.no'),
                            value: 'false',
                        },
                    ],
                    labelSize: LabelSize.ML,
                });
                break;
            case PERSON_FILTER_KEYS.country_code:
                if (countriesList) {
                    advancedFilters.push({
                        name: PERSON_FILTER_KEYS.country_code,
                        label: 'address.country',
                        type: FieldTypeApiEnum.SELECT,
                        size: FilterSize.THIRD,
                        values: sortCountriesForFilter(t, countriesList),
                        labelSize: LabelSize.ML,
                    });
                }
                break;
            case PERSON_FILTER_KEYS[
                `${discipline}_membership_unpaid` as
                    | 'football_membership_unpaid'
                    | 'futsal_membership_unpaid'
            ]:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS[
                        `${discipline}_membership_unpaid` as
                            | 'football_membership_unpaid'
                            | 'futsal_membership_unpaid'
                    ],
                    label: 'Members.onlyUnpaid',
                    type: FieldTypeApiEnum.CHECKBOX,
                    size: FilterSize.THIRD,
                    labelSize: LabelSize.ML,
                });
                break;
            case PERSON_FILTER_KEYS.user_is_email_verified:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.user_is_email_verified,
                    label: 'Members.onlyValidEmail',
                    type: FieldTypeApiEnum.CHECKBOX,
                    size: FilterSize.THIRD,
                    labelSize: LabelSize.ML,
                });
                break;
            case PERSON_FILTER_KEYS.organisation_unit_id:
                advancedFilters.push({
                    name: PERSON_FILTER_KEYS.organisation_unit_id,
                    label: 'general.authority',
                    type: FieldTypeApiEnum.AUTOCOMPLETE,
                    size: FilterSize.THIRD,
                    values: getOrganizationUnitSelectValues(organisationUnits),
                    labelSize: LabelSize.ML,
                    placeholder: 'filters.findByName',
                });
                break;
        }
    });

    return {
        fixed: {
            mainField: getMainFilter(searchPlaceholderKey, 'query', (value) =>
                validateSearchQuery(value, t)
            ),
            showAdvancedBtn: true,
        },
        advanced: advancedFilters,
    };
};
